<script setup lang="ts">
  import MenusService from '@/api/services/menus.service';
  import OptionsService from '@/api/services/options.service';
  import V2ModalOption from '@/components/modals/option/V2ModalOption.vue';
  import { modalOptionSettings } from '@/components/modals/option/modal-option-settings';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useCatalogUpdateListener } from '@/composition/catalog/use-catalog-update-listener';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { OptionType } from '@/enums/OptionType';
  import { IMenu } from '@/interfaces/IMenu';
  import { IOption } from '@/interfaces/IOption';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, getCurrentInstance, ref, watch } from 'vue';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const uploadsPath = Globals.uploadsPath;

  const options = ref<IOption[] | null>(null);
  const search = ref('');

  //#region Menu filter
  const menus = ref<IMenu[]>([]);
  const selectedMenuId = ref<number | 'all'>('all');
  const menusService = new MenusService();

  async function loadMenus() {
    if (!restaurantStore.restaurant) return;
    try {
      const allMenus = await menusService.getAll(restaurantStore.restaurant.id);
      menus.value = allMenus.filter((m) => !m.deletedAt);
    } catch (e) {
      console.error(e);
    }
  }

  const showMenuFilter = computed(() => menus.value.length > 1);

  const menuFilterItems = computed(() => [
    { value: 'all', text: i18nT('optionsPage.allMenus') },
    ...menus.value.map((m) => ({ value: m.id, text: m.name })),
  ]);
  //#endregion

  const loading = ref(false);
  /** Only the newest load writes: an older answer arriving last never wins. */
  let lastLoad = 0;
  async function loadOptions() {
    if (!restaurantStore.restaurant || !restaurantStore.selectedBranch) return;

    const load = ++lastLoad;
    try {
      loading.value = true;
      const loaded = await OptionsService.getAllUsable(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        'all',
        selectedMenuId.value === 'all' ? 'all' : selectedMenuId.value
      );
      if (load === lastLoad) options.value = loaded;
    } catch (e) {
      if (load === lastLoad) ui.serverError(e, i18nT('optionsPage.loadError'));
    } finally {
      if (load === lastLoad) loading.value = false;
    }
  }

  // Changes saved elsewhere (another device, the assistant) show here too.
  useCatalogUpdateListener(loadOptions);

  watch(
    [() => restaurantStore.restaurant?.id],
    () => {
      loadMenus();
    },
    { immediate: true }
  );

  watch(
    [
      () => restaurantStore.restaurant?.id,
      () => restaurantStore.selectedBranch?.id,
      selectedMenuId,
    ],
    () => {
      loadOptions();
    },
    { immediate: true }
  );

  const filteredOptions = computed(() => {
    if (!options.value) return [];
    const term = search.value.trim().toLowerCase();
    if (!term) return options.value;
    return options.value.filter((o) => o.title.toLowerCase().includes(term));
  });

  function usageText(option: IOption): string {
    const itemNames = (option.itemOptions ?? [])
      .map((io) => io.item?.name)
      .filter((name) => !!name);

    if (itemNames.length === 0) return i18nT('optionsPage.unused');
    if (itemNames.length === 1)
      return i18nT('optionsPage.usedInOne', { items: itemNames[0] });
    return i18nT('optionsPage.usedIn', {
      count: itemNames.length,
      items: itemNames.join(', '),
    });
  }

  function choicePictures(option: IOption): string[] {
    return (option.optionItems ?? [])
      .filter((oI) => !!oI.picture)
      .slice(0, 3)
      .map((oI) => `${uploadsPath}/${oI.picture}`);
  }

  function choiceCount(option: IOption): number {
    return (option.optionItems ?? []).length;
  }

  const MODAL_OPTION_SETTINGS = modalOptionSettings();

  function openNewOptionModal() {
    modals.show(
      V2ModalOption,
      {
        menuId: selectedMenuId.value === 'all' ? null : selectedMenuId.value,
      },
      MODAL_OPTION_SETTINGS,
      {
        saved: () => {
          loadOptions();
        },
      }
    );
  }

  function editOption(option: IOption) {
    modals.show(
      V2ModalOption,
      {
        optionToEdit: option,
        deletable: true,
      },
      MODAL_OPTION_SETTINGS,
      {
        saved: () => {
          loadOptions();
        },
        deleted: () => {
          loadOptions();
        },
      }
    );
  }
</script>

<template>
  <div class="menu-options-page">
    <div class="menu-options-page-inner">
      <!-- Command bar -->
      <div class="command-bar">
        <span class="page-title">{{ $t('optionsPage.title') }}</span>

        <div class="command-bar-controls">
          <PosUiInput
            class="search-input"
            :value="search"
            icon="magnify"
            size="sm"
            clearable
            :placeholder="$t('optionsPage.searchPlaceholder')"
            @input="search = $event"
          />

          <select
            v-if="showMenuFilter"
            v-model="selectedMenuId"
            class="menu-filter"
          >
            <option
              v-for="menuItem in menuFilterItems"
              :key="menuItem.value"
              :value="menuItem.value"
            >
              {{ menuItem.text }}
            </option>
          </select>

          <PosUiButton
            color="primary"
            size="sm"
            icon="plus"
            @click="openNewOptionModal"
          >
            {{ $t('optionsPage.newOption') }}
          </PosUiButton>
        </div>
      </div>

      <!-- List -->
      <div class="options-list">
        <template v-if="!options">
          <v-skeleton-loader type="table-row" />
          <v-skeleton-loader type="table-row" />
          <v-skeleton-loader type="table-row" />
        </template>

        <PosUiEmptyState
          v-else-if="filteredOptions.length === 0 && search.trim()"
          icon="magnify"
          :title="$t('optionsPage.emptySearchTitle')"
          :description="$t('optionsPage.emptySearchDescription')"
        />

        <PosUiEmptyState
          v-else-if="filteredOptions.length === 0"
          icon="format-list-bulleted"
          :title="$t('optionsPage.emptyTitle')"
          :description="$t('optionsPage.emptyDescription')"
        >
          <template #action>
            <PosUiButton
              color="primary"
              size="sm"
              icon="plus"
              @click="openNewOptionModal"
            >
              {{ $t('optionsPage.newOption') }}
            </PosUiButton>
          </template>
        </PosUiEmptyState>

        <template v-else>
          <button
            v-for="option in filteredOptions"
            :key="option.id"
            class="option-row"
            @click="editOption(option)"
          >
            <div class="option-row-main">
              <div class="option-row-heading">
                <span class="option-row-title">{{ option.title }}</span>
                <span class="option-row-count">
                  {{
                    $tc('itemOptions.choiceCount', choiceCount(option), {
                      count: choiceCount(option),
                    })
                  }}
                </span>
                <span
                  v-if="option.type === OptionType.Add"
                  class="option-row-badge"
                >
                  {{ $t('optionEditor.repeatableBadge') }}
                </span>
              </div>
              <span class="option-row-usage">{{ usageText(option) }}</span>
            </div>

            <div v-if="choicePictures(option).length" class="option-row-thumbs">
              <img
                v-for="(src, idx) in choicePictures(option)"
                :key="idx"
                class="option-row-thumb"
                :src="src"
                alt=""
              />
            </div>

            <PosUiIcon name="pencil-outline" class="option-row-edit" />
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .menu-options-page {
    @apply tw-flex tw-h-full tw-justify-center tw-px-4 tw-py-4 md:tw-px-6;

    .menu-options-page-inner {
      @apply tw-flex tw-w-full tw-max-w-7xl tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      .command-bar {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

        .page-title {
          @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
        }

        .command-bar-controls {
          @apply tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2;

          .search-input {
            @apply tw-w-52;
          }

          .menu-filter {
            @apply tw-h-9 tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-white tw-px-2 tw-text-sm tw-text-neutral-700 tw-outline-none;

            &:focus {
              @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
            }
          }
        }
      }

      .options-list {
        @apply tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto;

        .option-row {
          @apply tw-flex tw-min-h-16 tw-items-center tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-2.5 tw-text-left tw-transition-colors;

          &:hover {
            @apply tw-bg-neutral-50;
          }

          &:focus-visible {
            @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
          }

          &:last-child {
            @apply tw-border-b-0;
          }

          .option-row-main {
            @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

            .option-row-heading {
              @apply tw-flex tw-flex-wrap tw-items-baseline tw-gap-2;

              .option-row-title {
                @apply tw-text-sm tw-font-medium tw-text-neutral-900;
              }

              .option-row-count {
                @apply tw-text-xs tw-text-neutral-500;
              }

              .option-row-badge {
                @apply tw-rounded-full tw-bg-neutral-100 tw-px-2 tw-py-0.5 tw-text-xs tw-text-neutral-600;
              }
            }

            .option-row-usage {
              @apply tw-truncate tw-text-xs tw-text-neutral-500;
            }
          }

          .option-row-thumbs {
            @apply tw-flex tw-flex-none tw--space-x-2;

            .option-row-thumb {
              @apply tw-h-9 tw-w-9 tw-rounded-lg tw-border-2 tw-border-white tw-object-cover;
            }
          }

          .option-row-edit {
            @apply tw-flex-none tw-text-lg tw-text-neutral-400;
          }
        }
      }
    }
  }
</style>
